// While seats fill: the settings, the link, the seats (the host adds bots), and joining.
import React from 'react';
import type { LeagueView } from '../../leagues/api';
import type { Act } from './LeaguePage';
import { settingsRows, storedName, storeName } from './parts';

const DOTS = ['#8fb8dd', '#ec9a85', '#9fd3a6', '#c7a6e8', '#f2c46b', '#7fd1c7', '#e8a0c8', '#b8c47a'];

export function RecoveryBox({ code }: { code: string }) {
  return (
    <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--panel-line)', display: 'grid', gap: 4 }}>
      <span className="site-kicker">Your recovery code</span>
      <strong style={{ fontSize: 22, letterSpacing: '.08em', fontFamily: 'ui-monospace, Menlo, monospace' }}>{code}</strong>
      <span className="site-muted site-small">This browser remembers you. On another device, open the league link and enter this code to get your seat back. Keep it somewhere.</span>
    </div>
  );
}

// For someone on a new device (or who cleared their browser): get their seat back.
export function RecoverForm({ act, open = false }: { act: Act; open?: boolean }) {
  const [code, setCode] = React.useState('');
  return (
    <details open={open}>
      <summary style={{ cursor: 'pointer', color: 'var(--accent)' }}>Already in this league? Use your recovery code</summary>
      <form onSubmit={e => { e.preventDefault(); act('recover', { code }); }} style={{ display: 'flex', gap: 8, marginTop: 8, maxWidth: 420 }}>
        <input value={code} onChange={e => setCode(e.target.value)} placeholder="ABCD-EFGH" aria-label="Recovery code" style={{ flex: 1, minWidth: 0 }} />
        <button type="submit" className="site-btn" disabled={!code.trim()}>Get my seat</button>
      </form>
    </details>
  );
}

export default function LeagueEntry({ league: L, act }: { league: LeagueView; act: Act }) {
  const [name, setName] = React.useState(storedName);
  const [copied, setCopied] = React.useState(false);
  const [waiting, setWaiting] = React.useState<'join' | 'bot' | null>(null);
  const host = L.me?.isHost;
  const seats = Array.from({ length: L.settings.seats }, (_, i) => L.players[i] ?? null);
  const open = L.settings.seats - L.players.length;
  const hostName = L.players.find(p => p.id === L.hostId)?.name ?? 'The host';
  const link = `${window.location.origin}/league/${L.id}`;
  const copy = () => { navigator.clipboard?.writeText(link).catch(() => undefined); setCopied(true); window.setTimeout(() => setCopied(false), 2000); };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' }}>
      <section style={{ flex: '999 1 560px', minWidth: 0, display: 'grid', gap: 18 }}>
        <div>
          <p className="site-kicker">{host ? 'Your league · share the link to fill the seats' : L.me ? "You're in this league" : `${hostName} invited you to a league`}</p>
          <h1 className="site-display" style={{ fontSize: 52, lineHeight: 1.02, marginTop: 4, textTransform: 'none' }}>{L.name}</h1>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, maxWidth: 560 }}>
            <input readOnly value={link} aria-label="League link" onFocus={e => e.currentTarget.select()} style={{ flex: 1, minWidth: 0, minHeight: 44 }} />
            <button type="button" className="site-btn" onClick={copy}>{copied ? 'Copied!' : 'Copy link'}</button>
          </div>
        </div>
        <section aria-labelledby="sett" className="site-panel" style={{ padding: '6px 20px 10px' }}>
          <h2 id="sett" className="site-display" style={{ fontSize: 24, margin: '12px 0 8px' }}>Settings</h2>
          <dl style={{ margin: 0 }}>
            {settingsRows(L.settings).map(([k, v]) => (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 170px) minmax(0, 1fr)', gap: 12, padding: '10px 0', borderTop: '1px solid var(--panel-line)' }}>
                <dt className="site-kicker" style={{ paddingTop: 3 }}>{k}</dt><dd style={{ margin: 0 }}>{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      </section>

      <aside className="site-panel" style={{ flex: '1 1 340px', minWidth: 0, display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h2 className="site-display" style={{ fontSize: 26 }}>Seats</h2>
          <span className="site-muted" style={{ fontSize: 14 }}>{L.players.length} of {L.settings.seats} filled</span>
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {seats.map((p, i) => (
            <li key={p?.id ?? `open${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 48, padding: '6px 10px', borderRadius: 10,
              ...(p ? { background: 'var(--bg)', border: '1px solid var(--panel-line)' } : { border: '1px dashed var(--line)', color: 'var(--faint)' }) }}>
              <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: '50%', flex: 'none', background: p ? DOTS[i % DOTS.length] : 'transparent', color: '#24262e', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p?.name[0]?.toUpperCase()}</span>
              <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{p ? p.name : 'Open seat'}</span>
              <span className="site-muted" style={{ fontSize: 13 }}>{p ? (p.id === L.hostId ? 'Host' : p.bot ? 'Bot' : p.id === L.me?.id ? 'You' : '') : ''}</span>
              {!p && host && i === L.players.length && <button type="button" className="site-btn site-btn-small" disabled={waiting === 'bot'} onClick={async () => { setWaiting('bot'); await act('bot'); setWaiting(null); }}>{waiting === 'bot' ? 'Adding…' : 'Add a bot'}</button>}
              {p && host && p.id !== L.hostId && (
                <button type="button" className="site-icon-btn" aria-label={`Remove ${p.name}`} title={`Remove ${p.name}`} onClick={() => { if (p.bot || window.confirm(`Remove ${p.name} from the league?`)) act('remove', { playerId: p.id }); }}>✕</button>
              )}
            </li>
          ))}
        </ul>

        {!L.me && open > 0 && (
          <form onSubmit={async e => { e.preventDefault(); storeName(name.trim()); setWaiting('join'); await act('join', { name }); setWaiting(null); }} style={{ display: 'grid', gap: 8 }}>
            <label htmlFor="jname" className="site-kicker">Your name</label>
            <input id="jname" value={name} maxLength={30} onChange={e => setName(e.target.value)} style={{ minHeight: 48, fontSize: 17 }} />
            <button type="submit" className="site-btn site-btn-primary site-btn-big" disabled={!name.trim() || waiting === 'join'}>{waiting === 'join' ? 'Taking your seat…' : 'Take a seat'}</button>
          </form>
        )}
        {!L.me && open === 0 && <p className="site-muted">Every seat is taken. You can still follow the league from this link.</p>}
        {!L.me && <RecoverForm act={act} open />}
        {L.me && !host && (
          <>
            <p style={{ padding: '12px 14px', borderRadius: 10, background: '#26382c', color: '#cfeed4' }}>You're in. When every seat is filled, {hostName} starts the league and everyone builds an army here.</p>
            <button type="button" className="site-btn site-btn-small" style={{ justifySelf: 'start' }} onClick={() => { if (window.confirm('Give up your seat in this league?')) act('remove', { playerId: L.me!.id }); }}>Give up my seat</button>
          </>
        )}
        {host && (
          <div style={{ display: 'grid', gap: 8 }}>
            <button type="button" className="site-btn site-btn-primary site-btn-big" disabled={open > 0} onClick={() => act('start')}>Start the league</button>
            <p className="site-muted site-small">{open ? `Fill ${open} more seat${open > 1 ? 's' : ''} (people or bots) to start.` : 'Every seat is filled. Starting opens the army builder for everyone.'}</p>
          </div>
        )}
        {L.me && <RecoveryBox code={L.me.recovery} />}
      </aside>
    </div>
  );
}
